// The five reasons an andon can be withdrawn. Lifted from the Andon app's
// `lib/cancel.ts`; the labels are TR keys.
//
// The keys are Odoo's `cancel_reason` Selection, verbatim: the same five strings
// in Python, in a domain, in the BFF's request schema and on the chip the
// operator taps. A sixth copy of this list would be a sixth thing to keep in
// step, so the labels live here and nothing derives them from the key.
//
// WHY A FIXED LIST AND NOT A FREE-TEXT REASON.
//
// Because averaged into a single "cancelled" count these five say nothing at
// all, and each one is a finding about a different part of the system:
//
//   sorted_myself   the operator fixed it while waiting. The response was too
//                   slow to be worth waiting for — an SLA finding.
//   mistake         wrong tile, wrong pod, a pocket tap — a UI finding.
//   duplicate       somebody already raised it. A finding about whether the
//                   floor can see what has already been called in.
//   not_a_problem   real at the time, not a problem on inspection. A finding
//                   about what the operator could see from where they stood.
//   wrong_pod       right problem, wrong record — a finding about pod scanning.
//
// The optional line of text under the chips is the DETAIL. The chip is the
// finding, and only the chip is required.

import type { StringKey } from '@/i18n/strings';

export const CANCEL_REASONS = [
  { key: 'sorted_myself', label: 'cancelSortedMyselfT' },
  { key: 'mistake', label: 'cancelMistakeT' },
  { key: 'duplicate', label: 'cancelDuplicateT' },
  { key: 'not_a_problem', label: 'cancelNotAProblemT' },
  { key: 'wrong_pod', label: 'cancelWrongPodT' },
] as const satisfies ReadonlyArray<{ key: string; label: StringKey }>;

export type CancelReasonKey = (typeof CANCEL_REASONS)[number]['key'];

/** The chip's key, for a toast or a thread line. Undefined for a key this build has never heard of. */
export function cancelLabel(key: string): StringKey | undefined {
  return CANCEL_REASONS.find((r) => r.key === key)?.label;
}
